import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useContactsMutation, useMarkMessageAsReadMutation, useMarkMessageAsUnreadMutation, usePinChatMutation } from '../api/chatApi';
import Loader from '../../../components/Loader';
import { useSocket } from '../../../contexts/SocketContext';
import { useDispatch, useSelector } from 'react-redux';
// import TickIcon from '../../../assets/icons/TickIcon';
import { filterContacts, formatContactName, formatePhoneNumber, formatTimestamp, getDateLabel, messageLabel } from '../../../utils/helpers';
import Toast from '../../../components/ToastNotification';
import NewConversation from './NewConversation';
import { setContactsList } from '../slices/chatSlice';

const ChatList = ({ selectedId, contactState }) => {
  const session = useSelector((state) => state.auth?.session);
  const contactList = useSelector((state) => state.chat?.contacts);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoader, setLoader] = useState(false);
  const [conservationModal, setConservationModal] = useState(false);
  const {selectContact, setSelectContact } = contactState;
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOpenIndex, setModalOpenIndex] = useState(null);
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [modalContact, setModalContact] = useState(null);

  const [contactApi] = useContactsMutation();
  const [markAsReadApi] = useMarkMessageAsReadMutation();
  const [markAsUnreadApi] = useMarkMessageAsUnreadMutation();
  const [pinChatApi] = usePinChatMutation();
  const conservationRef = useRef(new Map());
  const [activeTab, setActiveTab] = useState('all');
  const socket = useSocket();
  const dispatch = useDispatch();

  const filteredContacts = useMemo(() => {
    const filterContactWithConservation = contactList.filter((con) => con.timestamp)
    let contacts = [...filterContactWithConservation];

    if (activeTab === 'unreply') {
      contacts = contacts.filter(cont => !cont.has_replied && !cont.is_group);
    } else if (activeTab === 'groups') {
      contacts = contacts.filter(cont => cont.is_group);
    } else if (activeTab === 'unread') {
      contacts = contacts.filter(cont => parseInt(cont.unread_count) !== 0 || cont.is_unread);
    }

    if (!searchTerm.trim()) return contacts;

    return filterContacts(contacts, searchTerm);
  },[searchTerm, contactList, activeTab]);

  const fetchContacts = useCallback(async () => {
    try {
      setLoader(true);
      const response = await contactApi({ id: selectedId }).unwrap();
      if (response?.data?.contacts) {
        const contacts = response.data?.contacts
        const contactArrList = contacts.map(con => ({
          ...con,
          name: formatContactName(con.name, con.push_name),
        }))

        conservationRef.current = new Map(contactArrList.map(contact => [contact.wa_id, contact]));
        dispatch(setContactsList(contactArrList));
      }
    } catch (error) {
      dispatch(setContactsList([]));
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoader(false);
    }
  }, [contactApi, selectedId]);

  const updateSortedContacts = useCallback(() => {
    const sorted = Array.from(conservationRef.current.values())
    .sort((a, b) => new Date(b.latest_message_time).getTime() - new Date(a.latest_message_time).getTime());

    dispatch(setContactsList([...sorted]));
  }, [dispatch]);

  const handleIncomingMessage = useCallback(
    async({ employeeId, msgObj, unread_count }) => {
      if(employeeId !== selectedId) return;

      const chatWaId = msgObj.is_group ? msgObj.recipient : msgObj.is_from_me ? msgObj.recipient : msgObj.sender;
      const existing = conservationRef.current.get(chatWaId);

      if (!existing) return;
      const updatedConversation = {
        ...existing,
        unread_count: unread_count,
        latest_message_time: msgObj.createdAt,
        latest_message: msgObj.content,
        is_from_me: msgObj.is_from_me,
        status: msgObj.status,
        type: msgObj.type,
        is_unread: true,
        timestamp: msgObj.timestamp
      }

      conservationRef.current.set(chatWaId, updatedConversation);

      updateSortedContacts();
  }, [ selectedId, updateSortedContacts]);

  const handleUnreadCountUpdate = ({employeeId, updatedContacts}) => {
    if (selectedId !== employeeId) return;

    updatedContacts.forEach((updated) => {
      const existing = conservationRef.current.get(updated.wa_id);
      if (existing) {
        conservationRef.current.set(updated.wa_id, {
          ...existing,
          // status: updated.status,
          unread_count: updated.unread_count
        });
      }
    });

    updateSortedContacts();
  }

  const handleNewContact = ({ employeeId, new_contact }) => {
    if (employeeId !== selectedId) return;

    const updateContact = {
      ...new_contact,
      name: Number(new_contact.name.charAt(0)) 
        ? (new_contact.push_name || new_contact.name )
        : new_contact.name
    }

    conservationRef.current.set(updateContact.wa_id, updateContact);
    updateSortedContacts();
  }

  const handleGroupUpdate = ({ employeeId, group_id, group_name }) => {
    if (employeeId !== selectedId) return;

    const existing = conservationRef.current.get(group_id);
    if (!existing) return;
    const updatedConversation = {
      ...existing,
      name: group_name
    }

    conservationRef.current.set(group_id, updatedConversation);
    Toast.success(`Group updated: ${group_name}`);
    updateSortedContacts();
  }

  const handleGroupCreate = ({ employeeId, group_id, group_name }) => {
    if (employeeId !== selectedId) return;
   
  }

  const handleContactUpdate = ({ employeeId, contact_update }) => {
    if (employeeId !== selectedId) return;

    const existing = conservationRef.current.get(contact_update.wa_id);
    if (existing) {
      const updatedConversation = {
        ...existing,
        // push_name: contact_update.push_name,
        has_replied: contact_update.has_replied,
        last_replied_at: contact_update.last_replied_at
      };

      conservationRef.current.set(contact_update.wa_id, updatedConversation);
    }
    updateSortedContacts();
  }

  const handleBtnClick = (e, index, contact) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    setModalPosition({ x: rect.right - 10, y: rect.bottom - 70 });

    if (modalOpen && modalOpenIndex === index) {
      setModalOpenIndex(null);
      setModalOpen(false);
    } else {
      setModalOpen(true);
      setModalOpenIndex(index);
      setModalContact(contact);
    }
  }

  const handleMarkMessageAsRead = async () => {
    try {
      setModalOpen(false);

      if (!modalContact?.is_unread) {
        await markAsUnreadApi({ user_id: selectedId, remote_jid: modalContact?.wa_id }).unwrap();
        Toast.success('Message mark as unread successfully')
      } else {
        await markAsReadApi({ user_id: selectedId, remote_jid: modalContact?.wa_id }).unwrap();
        Toast.success('Message mark as read successfully')
      }
    } catch (err) {
      Toast.error(err?.data?.message || err?.message || err?.error || 'Failed to fetch')
    }
  }

  const handleReadUnreadMessages = async ({ employeeId, contact }) => {
    if (selectedId !== employeeId) return;

    const existing = conservationRef.current.get(contact.wa_id);
    if (existing) {
      const updatedConversation = {
        ...existing,
        is_unread: contact.is_unread
      };

      conservationRef.current.set(contact.wa_id, updatedConversation);
    }
    updateSortedContacts();
  }

  const handlePinChat = async () => {
    try {
      const is_pinned = modalContact.is_pinned
      const response = await pinChatApi({user_id: selectedId, remote_jid: modalContact?.wa_id, type: is_pinned? 'unpinned' : 'pinned'}).unwrap();
      Toast.success(response.message);
    } catch (error) {
      Toast.error(error.data.message);
    } finally {
      setModalOpen(false);
    }
  }

  useEffect(() => {
    const closeOnOutsideClick = (e) => {
      if (!e.target.closest('.floating-modal') && !e.target.closest('.btn-icon')) {
        setModalOpen(false);
      }
    };
    if (modalOpen) document.addEventListener('click', closeOnOutsideClick);
    return () => document.removeEventListener('click', closeOnOutsideClick);
  }, [modalOpen]);

  useEffect(() => {
    if (!socket) return;

    socket.on('message-received', handleIncomingMessage);
    socket.on('unread_message_count', handleUnreadCountUpdate)
    socket.on('new-contact', handleNewContact);
    socket.on('contacts-upsert', ({ employeeId }) => {
      if (selectedId !== employeeId) return;
      setLoader(true);
      fetchContacts();
    })
    socket.on('chat_update', handleReadUnreadMessages)

    socket.on('group-update', handleGroupUpdate);
    socket.on('group-create', handleGroupCreate);
    socket.on('contact-update', handleContactUpdate);

    return () => {
      socket.off('message-received', handleIncomingMessage);
      socket.off('unread_message_count', handleUnreadCountUpdate);
      socket.off('contacts-upsert');
      socket.off('new-contact', handleNewContact);
      socket.off('group-update', handleGroupUpdate);
      socket.off('group-create', handleGroupCreate);
      socket.off('contact-update', handleContactUpdate);
      socket.off('chat_update', handleReadUnreadMessages)
    };
  },[socket, handleIncomingMessage, fetchContacts, selectedId]);
  
  useEffect(() => {
    fetchContacts();
  },[]);

  return (
    <>
      {/* <div className='tyn-appbar-wrap'>
        <div className='tyn-appbar-top'>

        </div>
        <div className='tyn-appbar-dwn'>
          <img src="/gear.png" alt="" />
        </div>
      </div> */}
      <div className='contact_container' style={{width : '110%'}}>
          <div  className='conversation_container' style={{left:conservationModal ? "0px" : "-100%"}}>
            <NewConversation 
              onClose = {() => setConservationModal(false)}
              contactState = {contactState}
              selectedId = {selectedId}
            />
          </div>
          
          <div className='conversation_section'>
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='search_input'
            />
            <div className='section' onClick={() => {
              setConservationModal(true)
              setSearchTerm('');
            }}>
              <img id='conversation_logo' src='/conversation.png' alt='' title='New Chat'/>
            </div>
          </div>

          <div style={styles.tabBar}>
            {['all', 'unreply','unread', 'groups'].map(type => (
              <button
                key={type}
                onClick={() => setActiveTab(type)}
                className={`tab_button ${activeTab === type ? 'active' : ''}`}
                style={{
                  fontWeight: 'bold'  ,
                  color: activeTab === type ? '#fff' : '#333'
                }}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>

          <div className='scrollContainer scrollable_list'>
            {isLoader && <Loader />}
            {filteredContacts.map((contact, index) => {
              const dateLabel = getDateLabel(contact?.timestamp);
              const displayDate =
                dateLabel === 'Today'
                  ? formatTimestamp(contact?.timestamp)
                  : dateLabel;
                
              return (
                <div 
                  key={`${contact.id} - ${contact.wa_id}`} 
                  className={`contactCard ${selectContact?.wa_id === contact.wa_id ? 'active' : ''}`} 
                  onClick={() => setSelectContact(contact)}
                  >
                    <div className='contact_body'>
                      <strong>{session?.phone_number === contact.name ? `${contact.name} (You)` : contact.name || formatePhoneNumber(contact.wa_id)}</strong>
                      <p className='latest_message'>
                        {/* {contact.is_from_me && (
                          <span>
                            {contact.status !== 1 && <TickIcon status={contact.status} />}
                          </span>
                        )} */}
      
                        {/* <span>{messageLabel(contact)}</span> */}
                        <span dangerouslySetInnerHTML={{ __html: messageLabel(contact) }} title={contact?.latest_message}/>
                      </p>
                    </div>
                    <div className='notification'>
                      <p style={{ color: contact.unread_count > 0 ? 'green': ''}}>{displayDate}</p>
                      <div className='notification-inner-div'>
                        {/* {(contact.unread_count > 0 || contact.is_unread) && ( */}
                          <div className={`unread_count ${(contact.unread_count > 0 || contact.is_unread) ? '' : 'opacity-0'}`}>
                            {contact.unread_count > 0 ? contact.unread_count : ''}
                          </div>
                        {/* )} */}
                        <div className={`btn-icon ${modalOpen && modalOpenIndex === index ? "show" : ""}`} 
                          onClick={(e) => handleBtnClick(e, index, contact)}
                        >
                          <img className='down-arrow-icon' src="/down-arrow.png" alt=""/>
                        </div>
                      </div>
                    </div>
                </div>
              )
            })
          }
          </div>
          {modalOpen && (
            <div
              className="floating-modal"
              style={{
                top: modalPosition.y,
                left: modalPosition.x,
              }}
            >
              <div className='option'>
                <div className="menu-item" onClick={handleMarkMessageAsRead}>
                  {modalContact.is_unread === false ? 'Mark as Unread' : '✅ Mark as read'}
                </div>
              </div>
              {/* <div className='pinned-section-icon'>
                <img className='pin-img' src="/pin.png" alt=''/>
              </div> */}
              <div className='option' onClick={handlePinChat}>
                <div className='pinned-section-icon'>
                  <img className='pin-img' src={modalContact.is_pinned ? "/unpin.png" : "/pin.png"} alt=''/>
                  <p className="pin-label">{modalContact.is_pinned ? 'Unpin' : 'Pin'}</p>
                </div>
              </div>
            </div>
          )}

      </div>
    </>
    
  );
};  

const styles = {
  hover:{
    backgroundColor: 'red',
  },
  tabBar: {
    display: 'flex',
    justifyContent: 'space-around',
    marginBottom: '10px',
    borderBottom: '1px solid #ccc',
    padding: '8px'
  }
};

export default ChatList;
